import { CopyOutlined } from "@ant-design/icons";
import { App, Button, Flex } from "antd";
import { FC, ReactNode, useEffect, useState } from "react";
import {
    getNetworkInfo,
    getPointInfo,
    getPointTypesList,
    getSettlementInfo,
} from "tm-shared/api";
import { Point, SettlementDetails } from "tm-shared/types";
import {
    buildRoutePath,
    isPointChains,
    isPointDistribution,
} from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useOfflineModeSelector } from "@/hooks/redux";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { InfoTabCapabilities } from "../AboutPoint.policy";
import { StyledAPTable, StyledAPTableLink, StyledAPTableRow } from "../Styled";

export const getDayWord = (num: number): string => {
    const absNum = Math.abs(num);
    switch (true) {
        case absNum % 10 === 1 && absNum % 100 !== 11:
            return `${num} день`;
        case [2, 3, 4].includes(absNum % 10) &&
            ![12, 13, 14].includes(absNum % 100):
            return `${num} дні`;
        default:
            return `${num} днів`;
    }
};

export interface KeyValueItem {
    label: ReactNode;
    value: ReactNode;
    copyValue?: string | object;
}

interface InfoTabProps {
    point: Point;
    capabilities: InfoTabCapabilities;
}

export const InfoTab: FC<InfoTabProps> = ({ point, capabilities }) => {
    const { notification } = App.useApp();
    const offlineMode = useOfflineModeSelector();

    const [network, setNetwork] = useState<{ name: string; id: string } | null>(
        null,
    );
    const [storageCity, setStorageCity] = useState<SettlementDetails | null>(
        null,
    );
    const [typesList, setTypesList] = useState<Record<string, string> | null>(
        null,
    );
    const [parent, setParent] = useState<Point | null>(null);
    const [loading, setLoading] = useState(true);

    const {
        type,
        id,
        title,
        context,
        market: { building, contragent, parentInfo },
    } = point;

    const geo = building.geo;

    useEffect(() => {
        if (offlineMode) {
            setLoading(false);
            return;
        }

        const getInfo = async () => {
            setLoading(true);

            const promises = [
                getPointTypesList().then(({ result }) => setTypesList(result)),
            ];

            if (isPointChains(point)) {
                promises.push(
                    getNetworkInfo({
                        network: point.typeContext.networkId,
                    }).then(({ result }) => setNetwork(result)),
                );
            }

            if (isPointDistribution(point)) {
                promises.push(
                    getSettlementInfo({
                        settlement: point.typeContext.storage.settlementId,
                    }).then(({ result }) => setStorageCity(result)),
                );
            }

            if (parentInfo?.parentId) {
                promises.push(
                    getPointInfo({
                        pointId: parentInfo.parentId,
                    }).then(({ result }) => setParent(result)),
                );
            }

            await Promise.allSettled(promises);
            setLoading(false);
        };

        getInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shelvesLength = context?.shelfSize?.total || 0;
    const shelvesLengthMeters = (shelvesLength / 100).toFixed(1);

    const items = [
        { label: "ID маркету", value: id, copyValue: id },

        isPointDistribution(point) && {
            label: "ID в обліковому записі партнера",
            value: point.typeContext.externalId,
            copyValue: point.typeContext.externalId,
        },

        { label: "Тип ТТ", value: typesList?.[type] ?? type },

        {
            label: "Загальна довжина полиць",
            value: `${shelvesLengthMeters}м (${shelvesLength}см)`,
        },
        {
            label: "Термін поставки",
            value: getDayWord(context?.deliveryDayInterval || 0),
        },
        {
            label: "Термін відстрочки оплати по Формі 1",
            value: getDayWord(context?.paymentF1DayInterval || 0),
        },
        {
            label: "Термін відстрочки оплати по Формі 2",
            value: getDayWord(context?.paymentF2DayInterval || 0),
        },

        ...(isPointDistribution(point)
            ? [
                  {
                      label: "Партнер",
                      value: point.typeContext.client.name,
                      copyValue: point.typeContext.client,
                  },
                  {
                      label: "Склад",
                      value: point.typeContext.storage.name,
                      copyValue: point.typeContext.storage,
                  },

                  ...(storageCity
                      ? [
                            {
                                label: "Місто складу",
                                value: storageCity.name,
                                copyValue: storageCity,
                            },
                        ]
                      : []),
              ]
            : []),

        title && { label: "Додаткова назва", value: title },

        geo && {
            label: "Координати",
            value: (
                <a
                    href={`https://www.google.com/maps/place/${geo}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textAlign: "right" }}
                >
                    {geo.split(",").join(", ")}
                </a>
            ),
        },

        contragent?.id && {
            label: "ІНН/ЄДРПОУ",
            value: contragent.name,
            copyValue: contragent,
        },

        parent && {
            label: "Батьківська торгова точка",
            value: (
                <StyledAPTableLink
                    to={buildRoutePath(ROUTES.PointDetails, {
                        pointId: parent.id,
                    })}
                >
                    {parent.name}
                </StyledAPTableLink>
            ),
        },

        network && {
            label: "Національна мережа",
            value: network.name,
            copyValue: network,
        },
    ].filter(Boolean) as KeyValueItem[];

    const links = [
        {
            to: buildRoutePath(ROUTES.PointContracts, { pointId: point.id }),
            text: "Контракти",
        },
        {
            to: buildRoutePath(ROUTES.PointShelves, { pointId: point.id }),
            text: "Конфігурація полиць",
        },
    ];

    if (capabilities.canSeePlanLink) {
        links.push({
            to: buildRoutePath(ROUTES.PointPlan, { pointId: point.id }),
            text: "План продажів",
        });
    }

    if (loading) {
        return <BlockLoader />;
    }

    return (
        <StyledAPTable>
            {items.map(({ label, value, copyValue }, index) => (
                <StyledAPTableRow key={`${String(label)}-${index}`}>
                    <PrimaryText>{label}</PrimaryText>

                    <Flex gap={4} align="center" justify="end">
                        {typeof value === "string" ? (
                            <PrimaryText fontWeight="300" textAlign="right">
                                {value}
                            </PrimaryText>
                        ) : (
                            value
                        )}

                        {copyValue && (
                            <Button
                                icon={<CopyOutlined />}
                                type="text"
                                onClick={() =>
                                    copyObjectToClipboard(
                                        copyValue,
                                        notification,
                                    )
                                }
                            />
                        )}
                    </Flex>
                </StyledAPTableRow>
            ))}

            {links.map(({ to, text }) => (
                <StyledAPTableRow key={to}>
                    <StyledAPTableLink to={to}>{text}</StyledAPTableLink>
                </StyledAPTableRow>
            ))}
        </StyledAPTable>
    );
};
